import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPosts, getBoardCodeById } from '../../api/boardApi';
import { AuthContext } from '../../context/AuthContext';
import { getCurrentUser } from '../../api/common';
import { getDept } from '../../api/deptApi';
import axios from 'axios';
import PageList from '../../components/List/PageList';

// 한 행 렌더링
function PostRow({post, navigate, currentPage, codeId }) {
  //console.log('Rendering PostRow for post:', post);
  const depth = post.depth || 0;
  const indentStyle = { marginLeft: `${depth * 30}px` };
  return (
    <tr
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/post/${post.id}?page=${currentPage}&code_id=${codeId}`)}
    >
      <td className="text-center">
        {post.file_count > 0 && <i className="fas fa-paperclip text-secondary" />}
      </td>
      <td>
        <h5 className="font-size-14 mb-0 d-inline" style={indentStyle}>
          {depth > 0 && <span className="text-muted me-1">┗</span>}
          {post.title}
          {post.comment_count > 0 && (
            <span className="text-primary ms-2">({post.comment_count})</span>
          )}
        </h5>
      </td>
      <td className="text-center">{post.writer_name}</td>
      <td className="text-center text-muted">
        {new Date(post.created_at).toLocaleString()}
      </td>
    </tr>
  );
}

function PostList() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [boardName, setBoardName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [boardPermissions, setBoardPermissions] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
    
  const postsPerPage = 10;
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const codeId = searchParams.get('code_id');

  const fetchCurrentUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const userData = await getCurrentUser(token);
      setCurrentUser(userData);
    } catch (err) {
      console.error('현재 사용자 정보 조회 실패:', err);
      setCurrentUser(null);
    }
  }, [token]);

  const fetchBoardPermissions = useCallback(async () => {
    if (!codeId || !currentUser?.id) return;
    
    try {
      // 현재 게시판에 대한 사용자 권한 조회
      const res = await axios.get(`/api/board-codes/auths/board-code-id/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBoardPermissions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('게시판 권한 조회 실패:', err);
      setBoardPermissions([]);
    }
  }, [codeId, currentUser?.id, token]);

  const fetchPosts = useCallback(
    async (page, keyword = '') => {
      const { posts: fetchedPosts, total: fetchedTotal } = await getPosts(
        page,
        postsPerPage,
        keyword,
        codeId
      );

      setPosts(fetchedPosts);
      setTotal(fetchedTotal);
      //console.log('Fetched posts:', fetchedPosts);
    },
    [postsPerPage, codeId]
  );

  useEffect(() => {
    const keyword = searchParams.get('search') || '';
    setSearchTerm(keyword);
    fetchPosts(currentPage, keyword);

    if (codeId) {
      getBoardCodeById(codeId).then((data) => {
        setBoardName(data.name);
      });
    }
  }, [currentPage, searchParams, fetchPosts, codeId]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchBoardPermissions();
    }
  }, [currentUser, fetchBoardPermissions]);

  // 권한 확인 useEffect
  useEffect(() => {
    const checkPermissions = async () => {
      // Admin 사용자는 항상 가능
      if (currentUser?.role === 'admin') {
        setCanCreate(true);
        return;
      }

      // 권한 데이터가 없는 경우 (조회 실패 또는 빈 배열) - 기본적으로 모든 사용자 허용
      if (!boardPermissions || boardPermissions.length === 0) {
        setCanCreate(true);
        return;
      }

      // read 권한이 있는지 확인
      const hasReadPermissions = boardPermissions.some(p => p.auth_type === 'read');

      if (!hasReadPermissions) {
        // read 권한 없음: 구분 없이 모두 볼 수 있음
        // canCreate 설정 (write/manage 권한 확인)
        let canWrite = false;
        for (const permission of boardPermissions) {
          if (permission.target_type === 'user' && String(permission.target_id) === String(currentUser?.userid)) {
            if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
              canWrite = true;
              break;
            }
          }
          
          if (permission.target_type === 'dept') {
            if (String(permission.target_id) === String(currentUser?.dept_code)) {
              if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
                canWrite = true;
                break;
              }
            }
            
            try {
              const deptData = await getDept(permission.target_id);
              if (deptData?.data?.full_child_dept_code) {
                const childDeptCodes = deptData.data.full_child_dept_code.split(',');
                if (childDeptCodes.includes(String(currentUser?.dept_code))) {
                  if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
                    canWrite = true;
                    break;
                  }
                }
              }
            } catch (err) {
              console.error('부서 정보 조회 실패:', err);
            }
          }
        }
        setCanCreate(canWrite);
        return;
      }

      // read 권한 있음: permission.auth_type에 상관없이 currentUser.userid 또는 currentUser.dept_code가 권한 목록의 target_id와 매칭되는지 확인
      let hasAccess = false;
      for (const permission of boardPermissions) {
        if (permission.target_type === 'user' && String(permission.target_id) === String(currentUser?.userid)) {
          hasAccess = true;
          break;
        }
        
        if (permission.target_type === 'dept') {
          if (String(permission.target_id) === String(currentUser?.dept_code)) {
            hasAccess = true;
            break;
          }
          
          try {
            const deptData = await getDept(permission.target_id);
            if (deptData?.data?.full_child_dept_code) {
              const childDeptCodes = deptData.data.full_child_dept_code.split(',');
              if (childDeptCodes.includes(String(currentUser?.dept_code))) {
                hasAccess = true;
                break;
              }
            }
          } catch (err) {
            console.error('부서 정보 조회 실패:', err);
          }
        }
      }

      if (!hasAccess) {
        // 대상이 아님: 이전 페이지로 이동
        navigate(-1);
        return;
      }

      // 접근 가능: canCreate 설정 (write/manage 권한 확인)
      let canWrite = false;
      for (const permission of boardPermissions) {
        if (permission.target_type === 'user' && String(permission.target_id) === String(currentUser?.userid)) {
          if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
            canWrite = true;
            break;
          }
        }
        
        if (permission.target_type === 'dept') {
          if (String(permission.target_id) === String(currentUser?.dept_code)) {
            if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
              canWrite = true;
              break;
            }
          }
          
          try {
            const deptData = await getDept(permission.target_id);
            if (deptData?.data?.full_child_dept_code) {
              const childDeptCodes = deptData.data.full_child_dept_code.split(',');
              if (childDeptCodes.includes(String(currentUser?.dept_code))) {
                if (permission.auth_type === 'write' || permission.auth_type === 'manage') {
                  canWrite = true;
                  break;
                }
              }
            }
          } catch (err) {
            console.error('부서 정보 조회 실패:', err);
          }
        }
      }
      setCanCreate(canWrite);
    };

    checkPermissions();
  }, [currentUser, boardPermissions, token, navigate]);

  const totalPages = Math.ceil(total / postsPerPage);

  const handleSearch = (e) => {
    e.preventDefault();
    const keyword = e.target.search.value.trim();
    setSearchTerm(keyword);
    navigate(`/board?page=1&search=${keyword}&code_id=${codeId}`);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* 헤더 영역 */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">📋 {boardName || '게시글'} 목록</h4>
            {canCreate && (
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/write?code_id=${codeId}`)}
              >
                새 글 작성
              </button>
            )}
          </div>

          {/* 검색 폼 */}
          <form className="row g-2 mb-3" onSubmit={handleSearch}>
            <div className="col-md-6">
              <input
                type="text"
                name="search"
                className="form-control"
                placeholder="제목 또는 작성자 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <button className="btn btn-outline-secondary" type="submit">
                검색
              </button>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  navigate(`/board?page=1&code_id=${codeId}`);
                }}
              >
                초기화
              </button>
            </div>
          </form>

          {/* 게시글 목록 + 페이지네이션 */}
          <PageList
            columns={[
              { label: '', style: { width: '36px' } },
              { label: '제목', style: { width: '56%' } },
              { label: '작성자', style: { width: '20%' }, className: 'text-center' },
              { label: '작성일', style: { width: '20%' }, className: 'text-center' },
            ]}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => navigate(`/board?page=${page}&search=${searchTerm}&code_id=${codeId}`)}
            emptyMessage="게시글이 없습니다."
          >
            {posts.map((p) => (
              <PostRow
                key={p.id}
                post={p}
                navigate={navigate}
                currentPage={currentPage}
                codeId={codeId}
              />
            ))}
          </PageList>
        </div>
      </div>
    </div>
  );
}

export default PostList;
